import store from "@src/reducers/store";
import type { NoVncVersion } from "./novnc-loader";

type PendingVncHandshake = {
  tab: Window;
  namespace?: string;
  wsUrl?: string;
  messageType: "VNC_KEY" | "VNC_RETRY";
  readyId?: string;
};

const pendingVncHandshakes = new Map<string, PendingVncHandshake>();
let hasVncReadyListener = false;

/**
 * true while the opener holds a wsUrl or still awaits the first ready
 * from the child tab
 */
export const hasPendingVncHandshake = (sessionId: string): boolean => {
  const entry = pendingVncHandshakes.get(sessionId);
  return Boolean(entry && (!entry.readyId || entry.wsUrl));
};

export const isExpectedVncTab = (
  sessionId: string,
  source: MessageEventSource,
): boolean => pendingVncHandshakes.get(sessionId)?.tab === source;

export const getVncNamespace = (sessionId: string): string | undefined =>
  pendingVncHandshakes.get(sessionId)?.namespace;

/**
 * record one page load handshake.
 * rejects messages from other windows and duplicate ready messages from the same page.
 */
export const markVncTabReady = (
  sessionId: string,
  source: MessageEventSource,
  readyId: string,
): boolean => {
  const entry = pendingVncHandshakes.get(sessionId);
  if (!entry || entry.tab !== source || entry.readyId === readyId) return false;

  entry.readyId = readyId;
  return true;
};

export const clearVncTab = (
  sessionId: string,
  source: MessageEventSource,
): void => {
  const entry = pendingVncHandshakes.get(sessionId);
  if (entry?.tab === source) pendingVncHandshakes.delete(sessionId);
};

export const clearVncKey = (
  sessionId: string,
  source: MessageEventSource,
): void => {
  const entry = pendingVncHandshakes.get(sessionId);
  if (entry?.tab === source) {
    entry.wsUrl = undefined;
  }
};

const trySendVncKey = (sessionId: string) => {
  const entry = pendingVncHandshakes.get(sessionId);
  if (!entry || !entry.readyId || !entry.wsUrl) return;

  if (entry.tab.closed) {
    pendingVncHandshakes.delete(sessionId);
    return;
  }

  const wsUrl = entry.wsUrl;
  try {
    entry.tab.postMessage(
      { type: entry.messageType, sessionId, wsUrl },
      window.location.origin,
    );
    console.info("[VNC] VNC_KEY sent to child tab", { sessionId });
  } catch (err) {
    console.error("Failed to send VNC key to new tab:", err);
  }
};

const ensureVncReadyListener = () => {
  if (hasVncReadyListener) return;

  const handleMessage = (e: MessageEvent) => {
    if (e.origin !== window.location.origin) return;

    const data = e.data;
    if (
      (data?.type === "VNC_CLOSED" ||
        data?.type === "VNC_CONNECTION_ESTABLISHED") &&
      data.sessionId
    ) {
      if (data.type === "VNC_CLOSED") {
        clearVncTab(data.sessionId, e.source as MessageEventSource);
      } else {
        clearVncKey(data.sessionId, e.source as MessageEventSource);
      }
      return;
    }

    if (
      data?.type !== "VNC_TAB_READY" ||
      !data.sessionId ||
      typeof data.readyId !== "string"
    ) {
      return;
    }

    if (!hasPendingVncHandshake(data.sessionId)) return;

    if (
      !markVncTabReady(
        data.sessionId,
        e.source as MessageEventSource,
        data.readyId,
      )
    ) {
      return;
    }

    trySendVncKey(data.sessionId);
  };

  window.addEventListener("message", handleMessage);
  hasVncReadyListener = true;
};

export interface OpenVncUrlParams {
  sourceId: string;
  sourceName: string;
  sourceStatus: "CONNECTED" | "DISCONNECTED";
  duration: string;
  sessionId: string;
  namespace: string;
  novncVersion?: NoVncVersion;
  swapRB?: boolean;
}

/**
 * Build the VNC page URL from the params available at click time.
 */
const buildVncUrl = ({
  sourceName,
  sourceId,
  sourceStatus,
  duration,
  sessionId,
  namespace,
  novncVersion = "1.3.0",
  swapRB = false,
}: OpenVncUrlParams): string => {
  const companyName =
    store.getState().yourSlice.authContext?.organization ?? "";
  const language = store.getState().yourSlice.language || "en";

  return `/remote-service/${sessionId}?companyName=${encodeURIComponent(
    companyName,
  )}&sourceName=${encodeURIComponent(sourceName)}&sourceId=${encodeURIComponent(
    sourceId,
  )}&sourceStatus=${encodeURIComponent(
    sourceStatus,
  )}&duration=${encodeURIComponent(duration)}&language=${encodeURIComponent(
    language,
  )}&namespace=${encodeURIComponent(
    namespace,
  )}&novncVersion=${encodeURIComponent(
    novncVersion,
  )}&swapRB=${encodeURIComponent(String(swapRB))}`;
};

/**
 * Open the VNC tab synchronously during a user gesture (click handler),
 * BEFORE any async work. This prevents Safari from blocking the popup.
 *
 * The tab navigates directly to the VNC route so the React page starts
 * loading immediately, no blank page.
 *
 * Returns the window reference to pass to sendVncKey() after the API call.
 */
export const openVncTab = (params: OpenVncUrlParams): Window | null => {
  ensureVncReadyListener();

  const url = buildVncUrl(params);

  // buildVncUrl always returns a fixed same-origin relative path, so the tab
  // can only ever open on our own origin.
  const tab = window.open(url, "_blank");
  if (!tab) {
    console.error("Popup blocked! Could not open VNC tab.");
    return tab;
  }

  pendingVncHandshakes.set(params.sessionId, {
    tab,
    namespace: params.namespace,
    messageType: "VNC_KEY",
  });

  return tab;
};

/**
 * Send the wsUrl to an already-open VNC tab via postMessage.
 * Call this after the async API call that fetches the auth key.
 */
export const sendVncKey = (
  tab: Window,
  sessionId: string,
  wsUrl: string,
  messageType: "VNC_KEY" | "VNC_RETRY" = "VNC_KEY",
): void => {
  ensureVncReadyListener();

  const current = pendingVncHandshakes.get(sessionId);
  // a slower response for an older tab must not replace a newer handshake
  if (current && current.tab !== tab) return;

  if (current) {
    pendingVncHandshakes.set(sessionId, {
      ...current,
      wsUrl,
      messageType,
    });
  } else {
    pendingVncHandshakes.set(sessionId, {
      tab,
      wsUrl,
      messageType,
    });
  }

  // best effort immediate send in case ready happened just before state update
  trySendVncKey(sessionId);
};
